import { state } from "../lib/state";
import { apiRequest } from "../lib/api";
import { showToast, formatError } from "../lib/dom";
import { onScreenChange } from "../lib/router";
import { trackEvent } from "../lib/analytics";
import * as projectImageCache from "../lib/projectImageCache";
import { openProjectPreview } from "./projectPreview";
import type { Project, ProjectsResponse } from "../types/api";

interface ImageEntry {
  url: string;
  revoke: () => void;
}

// Keep blob URL strings alive across closet re-renders so surviving tiles can
// reuse them synchronously and don't show a broken-image flash on each 12s poll.
const liveImages = new Map<string, ImageEntry>();

function revokeImage(requestId: string): void {
  const entry = liveImages.get(requestId);
  if (!entry) return;
  liveImages.delete(requestId);
  try {
    entry.revoke();
  } catch (error) {
    console.warn("Failed to revoke project image blob URL:", error);
  }
}

function revokeImagesNotIn(activeRequestIds: Set<string>): void {
  for (const requestId of [...liveImages.keys()]) {
    if (!activeRequestIds.has(requestId)) revokeImage(requestId);
  }
}

const POLL_INTERVAL_MS = 12_000;
const MAX_CONSECUTIVE_FAILURES = 3;
let consecutiveFailures = 0;

export function humanizeProjectType(type: string | undefined): string {
  switch (type) {
    case "outfit_v2":
    case "outfit_v1":
    case "outfit_prompt":
      return "Outfit";
    case "hair_change":
    case "hair_change_prompt":
      return "Hairstyle";
    case "combined":
      return "Outfit + Hair";
    default:
      return type || "Generation";
  }
}

function getProjectsList(): HTMLElement | null {
  return document.getElementById("projects-list");
}

function getProjectsEmpty(): HTMLElement | null {
  return document.getElementById("projects-empty");
}

export function renderProjects(): void {
  const projectsList = getProjectsList();
  const projectsEmpty = getProjectsEmpty();
  if (!projectsList || !projectsEmpty) return;

  const projects: Project[] = state.projects || [];
  // Drop revokers for projects that disappeared (deleted, list trimmed, etc).
  // Surviving tiles keep their existing blob URLs until the second pass below
  // hands them a fresh one — avoids a per-poll broken-image flash.
  revokeImagesNotIn(new Set(projects.map(p => p.requestId)));
  projectsList.innerHTML = "";
  projectsEmpty.hidden = projects.length > 0;

  projects.forEach(project => {
    const card = document.createElement("article");
    card.className = "panel project-card";
    const statusClass = String(project.status || "").toLowerCase();
    const createdAt = project.createdAt
      ? new Date(project.createdAt).toLocaleString()
      : "-";
    const cachedSrc = liveImages.get(project.requestId)?.url;
    const outputMarkup = project.outputUrl
      ? `<img data-project-image="${project.requestId}" alt="Generated result"${cachedSrc ? ` src="${cachedSrc}"` : ""} />`
      : `<div class="project-image-placeholder"><span>${project.type}</span></div>`;

    card.innerHTML = `
      <div class="project-image">${outputMarkup}</div>
      <div class="project-meta">
        <div class="project-topline">
          <div>
            <h3>${humanizeProjectType(project.type)}</h3>
            <p class="pricing-meta">Created ${createdAt}</p>
          </div>
          <span class="status-badge ${statusClass}">${project.status}</span>
        </div>
        ${project.error ? `<p class="pricing-meta">Error: ${project.error}</p>` : ""}
        <div class="project-actions">
          ${project.outputUrl ? `<button class="button button-secondary project-open-button" type="button" data-request-id="${project.requestId}" data-output-url="${project.outputUrl}" data-project-type="${project.type}" data-project-status="${project.status}">Open Result</button>` : ""}
          <button class="button button-secondary delete-project-button" type="button" data-request-id="${project.requestId}" data-project-type="${project.type}" data-project-status="${project.status}">Remove</button>
        </div>
      </div>
    `;

    projectsList.appendChild(card);
  });

  projects.forEach(project => {
    if (project.status !== "success" || !project.outputUrl) return;
    // Already painted from liveImages cache in the synchronous pass.
    if (liveImages.has(project.requestId)) return;
    const img = projectsList.querySelector<HTMLImageElement>(
      `img[data-project-image="${CSS.escape(project.requestId)}"]`,
    );
    if (!img) return;
    const requestId = project.requestId;
    const outputUrl = project.outputUrl;
    const fallbackUrls = project.outputUrls;
    void projectImageCache
      .getOrFetch(requestId, outputUrl, fallbackUrls)
      .then(({ blobUrl, revoke }) => {
        if (!img.isConnected) {
          revoke();
          return;
        }
        revokeImage(requestId);
        liveImages.set(requestId, { url: blobUrl, revoke });
        img.src = blobUrl;
      })
      .catch(error => {
        console.warn("Project image cache miss + fetch failed:", error);
        if (img.isConnected) img.src = outputUrl;
      });
  });

  document.querySelectorAll<HTMLButtonElement>(".project-open-button").forEach(button => {
    button.addEventListener("click", () => {
      const requestId = button.dataset.requestId;
      const outputUrl = button.dataset.outputUrl;
      const projectType = button.dataset.projectType ?? "";
      if (!requestId || !outputUrl) return;
      const project = (state.projects || []).find(p => p.requestId === requestId);
      trackEvent("closet_project_opened", {
        project_id: requestId,
        project_type: projectType,
        status: button.dataset.projectStatus,
      });
      openProjectPreview({
        requestId,
        type: projectType,
        outputUrl,
        fallbackUrls: project?.outputUrls,
      });
    });
  });

  document.querySelectorAll<HTMLButtonElement>(".delete-project-button").forEach(button => {
    button.addEventListener("click", async () => {
      const requestId = button.dataset.requestId;
      if (!requestId) return;
      try {
        await apiRequest(`/api/projects/${encodeURIComponent(requestId)}`, {
          method: "DELETE",
        });
        revokeImage(requestId);
        void projectImageCache.evict(requestId);
        trackEvent("closet_project_deleted", {
          project_id: requestId,
          project_type: button.dataset.projectType,
          status: button.dataset.projectStatus,
        });
        showToast("Removed.");
        await fetchProjects({ silent: true });
      } catch (error: unknown) {
        showToast(formatError(error), true);
      }
    });
  });
}

interface FetchProjectsOptions {
  silent?: boolean;
}

export async function fetchProjects(options: FetchProjectsOptions = {}): Promise<void> {
  const { silent = false } = options;
  if (!state.user || !state.idToken) {
    state.projects = [];
    renderProjects();
    return;
  }

  try {
    const data = await apiRequest<ProjectsResponse>("/api/projects");
    state.projects = data?.projects || [];
    renderProjects();
    consecutiveFailures = 0;
  } catch (error: unknown) {
    consecutiveFailures += 1;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      clearProjectsPolling();
      console.warn(
        `Closet polling stopped after ${MAX_CONSECUTIVE_FAILURES} failed fetches.`,
      );
    }
    if (!silent) showToast(formatError(error), true);
  }
}

export function clearProjectsPolling(): void {
  if (state.projectPollTimer !== null) {
    window.clearInterval(state.projectPollTimer);
    state.projectPollTimer = null;
  }
}

function scheduleProjectsPolling(): void {
  clearProjectsPolling();
  state.projectPollTimer = window.setInterval(() => {
    fetchProjects({ silent: true }).catch(console.error);
  }, POLL_INTERVAL_MS);
}

/**
 * Decide whether the closet should be polling based on the current screen
 * and auth state. Called on screen change and whenever auth state flips.
 */
export function evaluateClosetPolling(): void {
  const shouldPoll =
    state.activeScreen === "closet" &&
    Boolean(state.user) &&
    Boolean(state.idToken);

  if (!shouldPoll) {
    clearProjectsPolling();
    return;
  }

  // Reset failure counter on a fresh activation so a previous outage doesn't
  // permanently silence the closet.
  consecutiveFailures = 0;
  void fetchProjects({ silent: true });
  scheduleProjectsPolling();
}

export function bind(): void {
  const refreshButton = document.getElementById("refresh-projects-button");
  refreshButton?.addEventListener("click", () => {
    consecutiveFailures = 0;
    fetchProjects().catch(console.error);
  });

  onScreenChange(screen => {
    evaluateClosetPolling();
    if (screen === "closet") {
      trackEvent("closet_viewed", { project_count: state.projects.length });
    }
  });
}
